import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Flex,
  Group,
  Modal,
  NumberInput,
  PasswordInput,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from "mantine-react-table";
import { IconKey, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useDebouncedValue } from "@mantine/hooks";

type OuterUserRow = {
  id: string;
  username: string;
  email: string;
  age: number;
  businessSignupsCount: number;
  isActive: boolean;
  createdAt: string;
};

const PAGE_SIZE = 15;

export default function AdminOuterUsersPage() {
  const [rows, setRows] = useState<OuterUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 400);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: "",
    password: "",
    email: "",
    age: 25,
  });
  const [creating, setCreating] = useState(false);

  const [resetTarget, setResetTarget] = useState<OuterUserRow | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<OuterUserRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await trpc.admin.outerUserList.query({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: debouncedSearch.trim() || undefined,
      });
      setRows(res.items as OuterUserRow[]);
      setTotalRows(res.total);
    } catch (e) {
      handleTrpcError(e);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize, debouncedSearch]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const columns = useMemo<MRT_ColumnDef<OuterUserRow>[]>(
    () => [
      { accessorKey: "username", header: "Username", size: 140 },
      { accessorKey: "email", header: "Email", size: 220 },
      { accessorKey: "age", header: "Age", size: 72 },
      {
        accessorKey: "businessSignupsCount",
        header: "Signups",
        size: 90,
      },
      {
        accessorKey: "isActive",
        header: "Status",
        size: 100,
        Cell: ({ cell }) =>
          cell.getValue<boolean>() ? (
            <Badge color="green">Active</Badge>
          ) : (
            <Badge color="gray">Inactive</Badge>
          ),
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        size: 160,
        Cell: ({ cell }) => new Date(cell.getValue<string>()).toLocaleString(),
      },
      {
        id: "actions",
        header: "Actions",
        size: 120,
        enableSorting: false,
        Cell: ({ row }) => (
          <Group gap={4} wrap="nowrap">
            <Tooltip label="Reset password">
              <ActionIcon
                variant="subtle"
                color="orange"
                onClick={() => {
                  setNewPassword("");
                  setResetTarget(row.original);
                }}
              >
                <IconKey size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Delete">
              <ActionIcon variant="subtle" color="red" onClick={() => setDeleteTarget(row.original)}>
                <IconTrash size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        ),
      },
    ],
    []
  );

  const handleCreate = async () => {
    const username = createForm.username.trim().toLowerCase();
    if (username.length < 3) {
      notifications.show({ title: "Validation", message: "Username must be at least 3 characters", color: "red" });
      return;
    }
    if (createForm.password.length < 8) {
      notifications.show({ title: "Validation", message: "Password must be at least 8 characters", color: "red" });
      return;
    }
    if (!createForm.email.trim()) {
      notifications.show({ title: "Validation", message: "Email is required", color: "red" });
      return;
    }
    setCreating(true);
    try {
      await trpc.admin.outerUserCreate.mutate({
        username,
        password: createForm.password,
        email: createForm.email.trim(),
        age: createForm.age,
      });
      notifications.show({ title: "Created", message: "Outer user created", color: "green" });
      setCreateOpen(false);
      setCreateForm({ username: "", password: "", email: "", age: 25 });
      void fetchRows();
    } catch (e) {
      handleTrpcError(e);
    } finally {
      setCreating(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    if (newPassword.length < 8) {
      notifications.show({ title: "Validation", message: "Password must be at least 8 characters", color: "red" });
      return;
    }
    setResetting(true);
    try {
      await trpc.admin.outerUserResetPassword.mutate({
        id: resetTarget.id,
        newPassword,
      });
      notifications.show({
        title: "Updated",
        message: "Password reset; existing sessions invalidated.",
        color: "green",
      });
      setResetTarget(null);
      setNewPassword("");
    } catch (e) {
      handleTrpcError(e);
    } finally {
      setResetting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await trpc.admin.outerUserDelete.mutate({ id: deleteTarget.id });
      notifications.show({ title: "Deleted", message: "Outer user removed", color: "green" });
      setDeleteTarget(null);
      void fetchRows();
    } catch (e) {
      handleTrpcError(e);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Flex direction="column" gap="md" p="md">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <div>
          <Title order={2}>Outer users</Title>
          <Text size="sm" c="dimmed">
            Business-card onboarding partners: create accounts, reset passwords, or remove access.
          </Text>
        </div>
        <Button leftSection={<IconPlus size={18} />} onClick={() => setCreateOpen(true)}>
          Create outer user
        </Button>
      </Group>

      <MantineReactTable
        columns={columns}
        data={rows}
        state={{ isLoading: loading, pagination }}
        manualPagination
        rowCount={totalRows}
        onPaginationChange={setPagination}
        enableGlobalFilter={false}
        enableColumnFilters={false}
        initialState={{ density: "xs" }}
        mantineTableContainerProps={{ style: { minHeight: 360 } }}
        renderTopToolbarCustomActions={() => (
          <TextInput
            placeholder="Search username or email"
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
            leftSection={<IconSearch size={16} />}
            style={{ minWidth: 260 }}
          />
        )}
      />

      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title="Create outer user" size="md">
        <Flex direction="column" gap="sm">
          <TextInput
            label="Username"
            value={createForm.username}
            onChange={(e) => setCreateForm((f) => ({ ...f, username: e.currentTarget.value }))}
            required
          />
          <PasswordInput
            label="Password"
            value={createForm.password}
            onChange={(e) => setCreateForm((f) => ({ ...f, password: e.currentTarget.value }))}
            required
          />
          <TextInput
            label="Email"
            type="email"
            value={createForm.email}
            onChange={(e) => setCreateForm((f) => ({ ...f, email: e.currentTarget.value }))}
            required
          />
          <NumberInput label="Age" min={1} max={150} value={createForm.age} onChange={(v) => setCreateForm((f) => ({ ...f, age: typeof v === "number" ? v : f.age }))} />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button loading={creating} onClick={() => void handleCreate()}>
              Create
            </Button>
          </Group>
        </Flex>
      </Modal>

      <Modal
        opened={!!resetTarget}
        onClose={() => {
          setResetTarget(null);
          setNewPassword("");
        }}
        title={`Reset password — ${resetTarget?.username ?? ""}`}
      >
        <PasswordInput
          label="New password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.currentTarget.value)}
          required
        />
        <Group justify="flex-end" mt="md">
          <Button
            variant="default"
            onClick={() => {
              setResetTarget(null);
              setNewPassword("");
            }}
          >
            Cancel
          </Button>
          <Button loading={resetting} color="orange" onClick={() => void handleResetPassword()}>
            Reset password
          </Button>
        </Group>
      </Modal>

      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete outer user">
        <Text size="sm">
          Delete <strong>{deleteTarget?.username}</strong> ({deleteTarget?.email})? This cannot be undone.
        </Text>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button loading={deleting} color="red" onClick={() => void handleDelete()}>
            Delete
          </Button>
        </Group>
      </Modal>
    </Flex>
  );
}
